'use client'

import React, { forwardRef, memo, useImperativeHandle } from 'react'
import { useTranslations } from 'next-intl'
import { TAddressFormValues } from '@segway/core'
import { CartAddressInput, CustomerAddressInput } from '@segway/core/src/graphql/generated/graphql'
import isEmpty from 'lodash/isEmpty'

import {
  Button,
  Checkbox,
  FloatingLabelSelect,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@/ui'

import { CheckoutAddressValidationDialog } from '../../app/[locale]/checkout/_components/checkout'
import useUserAddressForm from '../../hooks/userAddressForm/useUserAddressForm'

interface UserAddressFormProps {
  submitText?: string
  onSubmit?: (values: CartAddressInput & { default?: boolean }) => void
  defaultValues?: CartAddressInput | null
  saveBtnVisible?: boolean
  saveToAddressBtnVisible?: boolean
  saveAsDefaultBtnVisible?: boolean
}

/**
 * 用户地址表单 组件
 *
 * @param {string} [submitText] - 提交按钮的文案，默认为'Save'
 * @param {object} [defaultValues] - 默认地址
 * @param {function(values):void} [onSubmit] - 表单提交且通过校验后的回调, 参数为表单所有字段数据
 * @param {boolean} [saveBtnVisible] - 是否展示保存按钮
 * @param {boolean} [saveToAddressBtnVisible] - 是否展示保存到地址栏按钮
 * @param {boolean} [saveAsDefaultBtnVisible] - 是否展示保存为默认地址按钮
 *
 * @returns {JSX.Element} Form表单组件
 *
 */

export interface UserAddressFormRef {
  handleSubmit: (cb: (values: TAddressFormValues) => Promise<void>) => void
  resetForm: (values: TAddressFormValues) => void
}

const UserAddressForm = forwardRef<UserAddressFormRef, UserAddressFormProps>(
  (
    {
      submitText = 'Save',
      onSubmit = () => {},
      defaultValues,
      saveBtnVisible = true,
      saveToAddressBtnVisible = false,
      saveAsDefaultBtnVisible = false,
    },
    ref,
  ) => {
    const {
      form,
      formSubmit,
      countriesOptions,
      regionsOptions,
      addressValidator,
      closeAddressValidator,
    } = useUserAddressForm(onSubmit, defaultValues)
    useImperativeHandle(ref, () => ({
      handleSubmit: (cb: (values: TAddressFormValues) => Promise<void>) => {
        form.handleSubmit(cb)()
      },
      resetForm: (values: TAddressFormValues) => {
        form.reset(values)
      },
    }))

    const t = useTranslations('Address')

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(formSubmit)} className="w-full space-y-3 xl:space-y-4">
          <div className="grid grid-cols-2 gap-2 xl:gap-3">
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input label={t('first_name')} required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input label={t('last_name')} required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('company')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="street.0"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('street_address')} required {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="street.1"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('second_line_of_address')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="street.2"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('second_line_of_address')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="country_code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FloatingLabelSelect
                    label={t('country_and_region')}
                    required
                    options={countriesOptions}
                    onValueChange={field.onChange}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('city')} required {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {isEmpty(regionsOptions) ? (
            <FormField
              control={form.control}
              name="region"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input label={t('state')} required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name="region_id"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelSelect
                      label={t('state')}
                      required
                      options={regionsOptions}
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="postcode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('postal_code')} required {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telephone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input label={t('telephone_number')} required {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div>
            {saveAsDefaultBtnVisible && (
              <FormField
                control={form.control}
                name="default"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center pb-2 pt-4">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        <span className="ml-1 text-[12px]">{t('set_as_default_address')}</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {saveToAddressBtnVisible && (
              <FormField
                control={form.control}
                name="save_in_address_book"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center pb-4 pt-2">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        <span className="mb-[-2px] ml-1 text-[12px]">
                          {t('save_changed_address_to_my_book')}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          {saveBtnVisible && (
            <Button type="submit" className="h-[44px] w-full text-[14px] xl:mt-[24px] xl:w-[215px]">
              {submitText}
            </Button>
          )}
        </form>
        <CheckoutAddressValidationDialog
          isOpen={addressValidator.modalVisible}
          address={addressValidator.address}
          improvedAddress={addressValidator.improvedAddress}
          addressComparison={addressValidator.addressComparison}
          errorMessage={addressValidator.errorMessage}
          onClose={closeAddressValidator}
          onSaveAddress={(address: CustomerAddressInput) => {
            closeAddressValidator()
            addressValidator?.saveCallback?.(address)
          }}
        />
      </Form>
    )
  },
)

UserAddressForm.displayName = 'UserAddressForm'

export default memo(UserAddressForm)
